import { useEffect } from 'react';
import type { RouteMeta } from '@shared/seo/seoMeta';

/**
 * Marks the `<head>` elements this hook creates so it can find and reuse
 * them across route changes (rather than appending a fresh copy each time)
 * without clobbering unrelated tags. The server-injected tags don't carry
 * it; that's fine — the upserts below match on `name` / `rel`, so they
 * update the server's elements in place and only stamp this marker on the
 * ones they create themselves.
 */
const MANAGED_ATTR = 'data-rh';

/**
 * Find the existing `<meta name="...">` in `<head>` and update its
 * `content`, or create + append one (tagged with {@link MANAGED_ATTR}) if
 * none exists. Matching on `name` — not the marker — means we adopt and
 * update the server-rendered tag instead of appending a duplicate.
 */
function upsertMetaByName(name: string, content: string): void {
  const head = document.head;
  let el = head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    el.setAttribute(MANAGED_ATTR, '');
    head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/** Upsert the canonical `<link>`, updating any existing one in place. */
function upsertCanonical(href: string): void {
  const head = document.head;
  let el = head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    el.setAttribute(MANAGED_ATTR, '');
    head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Drop the robots `<meta>` when a route is no longer `noindex`. Removal is
 * deliberately NOT scoped to {@link MANAGED_ATTR}: the server injects a
 * robots tag (without the marker) for `noindex` routes on the initial load,
 * and an indexable page must never keep a stale `noindex` directive after
 * the user navigates to it — so we clear whichever robots tag is present.
 */
function removeRobotsMeta(): void {
  const el = document.head.querySelector('meta[name="robots"]');
  if (el) {
    el.remove();
  }
}

/**
 * Keep `document.title`, the description, canonical and robots `<head>` tags
 * in sync with the resolved route meta during client-side SPA navigation.
 *
 * The server injects real (non-React) head tags for the FIRST HTML load;
 * react-router route changes never refetch that HTML, so this hook updates
 * those same elements IN PLACE on each route change. Updating in place (vs.
 * rendering React 19 native `<title>`/`<meta>`, which React won't reconcile
 * against the server's plain-HTML tags) guarantees there is never a
 * duplicate `<title>` and the browser tab always reflects the active route.
 *
 * No-ops while `meta` is null/undefined (e.g. a play page whose chart hasn't
 * loaded yet), leaving the previously-rendered head untouched. The effect is
 * keyed on the meta fields — not the object identity — so callers can pass a
 * freshly-built meta object on every render without re-running it needlessly.
 */
export function useDocumentHead(meta: RouteMeta | null | undefined): void {
  const title = meta?.title;
  const description = meta?.description;
  const canonical = meta?.canonical;
  const noindex = meta?.noindex ?? false;
  useEffect(() => {
    if (title === undefined || description === undefined || canonical === undefined) {
      return;
    }
    document.title = title;
    upsertMetaByName('description', description);
    upsertCanonical(canonical);
    if (noindex) {
      upsertMetaByName('robots', 'noindex, follow');
    } else {
      removeRobotsMeta();
    }
  }, [title, description, canonical, noindex]);
}
